import { Helmet } from 'react-helmet-async'
import { abs, DEFAULT_OG_IMAGE } from '../../lib/seo'

interface SeoProps {
  title: string
  description: string
  /** Site-relative canonical path, e.g. /blog/foo (no trailing slash). */
  canonicalPath: string
  image?: string
  ogType?: 'website' | 'article'
  /** JSON-LD objects to embed (BlogPosting, BreadcrumbList, WebApplication, …). */
  jsonLd?: object[]
  noindex?: boolean
}

/**
 * Base SEO head for content pages. Mirrors the Helmet/JSON-LD pattern used by
 * NewLandingPage.tsx (title/desc/canonical/OG/Twitter + ld+json scripts).
 */
export default function Seo({
  title,
  description,
  canonicalPath,
  image = DEFAULT_OG_IMAGE,
  ogType = 'website',
  jsonLd = [],
  noindex = false,
}: SeoProps) {
  const url = abs(canonicalPath)
  const ogImage = abs(image)
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {jsonLd.map((obj, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(obj)}
        </script>
      ))}
    </Helmet>
  )
}
